<template>
  <v-container class="pa-0 table-container">
    <slot></slot>
    <v-data-table v-model="selectedItems"
                  :headers="headers"
                  :items="items"
                  :item-class="itemClass"
                  :sort-by="sortBy"
                  @update:sort-by="sortByUpdated"
                  :sort-desc.sync="sortDescending"
                  item-key="id"
                  :height="height"
                  calculate-widths
                  :show-select="showSelect"
                  :single-select="singleSelect"
                  :class="[{selectAdjustment: showSelect}, 'res-basic-table']"
                  :dense="dense"
                  hide-default-header
                  hide-default-footer
                  :page.sync="optionsPage"
                  :items-per-page="optionsItemsPerPage"
                  :loading="loading"
                  @page-count="pageCount = $event">
      <template v-slot:header="{ props: { headers } }">
        <tr>
          <th v-for="(item, index) in headers"
              :key="item.value"
              role="columnheader"
              scope="col"
              :class="['column', item.sortable ? 'sortable' : '', `text-${item.align || 'start'}`, item.value == 'action' ? 'res-data-table-actions' : '']"
              @click.stop="item.sortable ? sort(item.value) : ''">
            <div :key="index">
              <span class="nowrap resGrey--text text--darken-1 mr-2">{{ item.text }}</span>
              <v-icon v-if="item.sortable" x-small color="resGrey darken-1" :key="index">{{sortingIcon(item.value)}}</v-icon>
            </div>
          </th>
        </tr>
      </template>
      <template v-for="slotName in formattedSlotOptions" v-slot:[slotName]="{ item }">
        <slot :name="slotName" :item="item"></slot>
      </template>
    </v-data-table>
    <v-layout row class="res-basic-table-footer align-center">
      <v-pagination v-show="pageCount > 1"
                    v-model="optionsPage"
                    color="resGrey darken-1"
                    type="number"
                    @input="setOptionsPage"
                    :length="pageCount"
                    class="pl-3">
      </v-pagination>
      <v-spacer></v-spacer>
        <res-text-field class="res-basic-table-footer__rows-per-page" v-model="calculatedItemsPerPage"></res-text-field>
        <label class="pr-4 pl-2 pagination-rows-display">Rows {{ startRow }} - {{ maxRow }} ({{ optionsTotalItems }} Total)</label>
    </v-layout>
  </v-container>
</template>

<script>
  import ResTextField from '@/components/base/ResTextField.vue';
  import Sortable from 'sortablejs'

  export default {
    name: 'ResDataTable',
    components: {    
      ResTextField
    },
    data() {
      return {
        sortBy: "",
        sortDescending: false,
        optionsItemsPerPage: this.baseRowPerPage || 10,
        optionsPage: 1,
        pageCount: 0,
        selectedItems: [],
      }
    },
    props: {
      height: {
        type: String
      },
      headers: {
        type: Array
      },
      items: {
        type: Array
      },
      showSelect: {
        type: Boolean,
        default: false
      },
      singleSelect: {
        type: Boolean,
        default: false
      },
      slotOptions: {
        type: Array,
        default() {
          return []
        }
      },
      dense: {},
      loading: {
        type: Boolean,
        default: false
      },
      itemClass: {
        type: [String, Function]
      },
      draggable: {
        type: Boolean,
        default: false
      },
      defaultSortBy: {
        type: String,
        default:''
      },
      defaultSortDescending: {
        type: Boolean,
        default: true
      }
    },
    computed: {
      formattedSlotOptions() {
        let slotOptions = this.slotOptions.map(optionName => {
          return `item.${optionName}`
        })
        return slotOptions
      },
      startRow() {
        return this.optionsPage === 1
          ? 1 : ((this.optionsPage - 1) * this.calculatedItemsPerPage) + 1
      },
      maxRow() {
        if (this.optionsItemsPerPage == null) return 0
        return this.optionsPage * this.optionsItemsPerPage < this.optionsTotalItems
          ? this.optionsPage * this.optionsItemsPerPage : this.optionsTotalItems
      },
      calculatedItemsPerPage: {
        get() {
          if ((!this.optionsItemsPerPage && this.baseRowPerPage) || this.optionsItemsPerPage < 1) {
            return 10;
          } else {
            return Number(this.optionsItemsPerPage);
          }
        },
        set(newValue) {
          if (isNaN(newValue)) {
            this.optionsItemsPerPage = 5;
            return;
          }

          this.optionsItemsPerPage = Number(newValue);
        }
      },
      optionsTotalItems() {
        if (this.items) {
          return this.items.length;
        }
        else {
          return 0;
        }
      }
    },
    methods: {
      dynamicHeaderIcon(name) {
        return `header.${name}`
      },
      dropped(e) {
        this.$emit('sorted', e);
      },
      sortingIcon(section) {
        if (this.sortBy === section) {
          if (this.sortDescending === true) {
            return "fas fa-arrow-down ignore-aria-sort"
          }
          return "fas fa-arrow-up ignore-aria-sort"
        }
        return "fas fa-sort ignore-aria-sort"
      },
      defaultSort() {
        this.sortBy = this.sortBy || this.defaultSortBy;
        this.sortDescending = this.defaultSortDescending || false;
        var sortByHeader = this.headers.filter((h) => h.value == this.sortBy)[0];
        if (sortByHeader) {
          this.sort(sortByHeader.value);
        }
      },
      sort(section) {
        if (this.sortBy === section && this.sortDescending === false) {
          this.sortDescending = true
          return
        } else if (this.sortBy === section) {
          this.sortDescending = false
          return
        }
        this.sortBy = section
        this.sortDescending = false
      },
      setOptionsPage(val) {
        this.optionsPage = val
      },
      sortByUpdated(val) {
        this.sortBy = val;
        this.$emit('update:sort-by', this.sortBy);
      }
    },
    watch: {
      optionsItemsPerPage: function() {
        this.optionsPage = 1
        this.optionsItemsPerPage = this.calculatedItemsPerPage
      },
      resetPage: function () {
        this.optionsPage = 1
      },
      selectedItems() {
        this.$emit('selected-items', this.selectedItems)
      },
      items() {
        this.defaultSort();
      },
      draggable(newVal, oldVal) {
        if (newVal && !oldVal) {
          const options = {
            animation: 150,
            onUpdate: function (event) {
              this.dropped(event)
            }.bind(this)
          }
          Sortable.create(this.$el.getElementsByTagName('tbody')[0], options)
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .v-data-table {
    tbody tr:not(:last-child) td:not(.v-data-table__mobile-row) { border-bottom: 1px solid var(--v-resGrey-lighten3); }
    border: solid 1px var(--v-resGrey-lighten1);
    box-shadow: none;
    -webkit-box-shadow: none;
  }

  .v-data-table th {
    cursor: default;
    height: 35px;
    background-color: var(--v-resGrey-lighten3);
    padding-left: 4px;
  }

  .res-basic-table {
    &-footer {
      background-color: white;
      border: 1px solid rgba(0,0,0,.12);
      border-top: none;
      height: 70px;
      font-size: 12px;
      margin-left: 0px;
      margin-right: 0px;
      ::v-deep .v-pagination__navigation{
        height:20px;
        width:20px !important;
        min-width:20px !important;
        box-shadow: none;
        margin-top: 0;
        margin-bottom: 0;
        &:focus{
          outline: none !important;
        }
        .v-icon {
          height:20px;
          width:20px !important;
          min-width:20px !important;
          font-size: 14px;
          color: var(--v-resGrey-darken1)
        }
      }
      ::v-deep .v-pagination__item{
        height:20px;
        min-width:20px !important;
        box-shadow: none;
        border: 1px solid var(--v-resGrey-lighten3);
        font-size: 12px;
      }
      &__rows-per-page{
        flex: none;
        padding: 0 !important;
        margin: 0 !important;
        ::v-deep .v-input__slot{
          min-height: 30px !important;
          display: flex;
          width: 30px;
          border-radius: 6px;
          border-width: 1px;
          text-align: left;
          padding: 0 !important;
          &:before {
            border-style: none;
          }
        }
        ::v-deep input{
          margin-top: 0px;
          font-size: 13px;
          text-align: center;
        }
      }
      &-header__select-all  {
        ::v-deep .v-icon {
          font-size: 18px !important;
        }
      }
    }
  ::v-deep .text-start {
      min-width: 100px;
    max-width: 500px;
    white-space: normal;
    overflow-wrap: break-word;
    word-wrap: break-word;   
    word-break: break-word;
  }

  .res-data-table-actions {
    min-width: 125px;
  }
  }
</style>
